import { useMemo } from "react";
import { addDays, format, isSameDay, parseISO, startOfWeek } from "date-fns";
import { cn } from "@/lib/utils";
import type { TaskRead } from "@/api/types";

/** This week's load, one bar per day of the open tasks due that day; today in solid
 *  indigo, the other days pale (after the owner's nidria front, 2026-09-29). */
export function WeekLoadCard({ tasks }: { tasks: TaskRead[] }) {
  const days = useMemo(() => {
    const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(monday, i);
      return {
        day,
        count: tasks.filter((t) => t.due_at && isSameDay(parseISO(t.due_at), day)).length,
        today: isSameDay(day, new Date()),
      };
    });
  }, [tasks]);
  const max = Math.max(1, ...days.map((d) => d.count));
  const total = days.reduce((sum, d) => sum + d.count, 0);

  return (
    <section className="surface-plane p-5">
      <div className="flex items-center justify-between">
        <h2 className="type-zonehead">My week · 7 days</h2>
        <span className="text-xs text-slate-400">
          {total === 0 ? "Nothing due this week" : `${total} due this week`}
        </span>
      </div>
      <div className="mt-5 grid grid-cols-7 items-end gap-3">
        {days.map(({ day, count, today }) => (
          <div key={day.toISOString()} className="flex flex-col items-center gap-2">
            <span className={cn("text-xs tabular-nums", today ? "font-semibold text-indigo-700" : "text-slate-400")}>
              {count}
            </span>
            <div className="flex h-20 w-full items-end justify-center">
              <div
                className={cn(
                  "w-full max-w-[44px] rounded-lg",
                  count === 0
                    ? today
                      ? "bg-indigo-200"
                      : "bg-slate-100"
                    : today
                      ? "bg-indigo-600"
                      : "bg-indigo-100",
                )}
                // An empty day keeps a low stub, so the week still reads as a week.
                style={{ height: count === 0 ? "6px" : `${Math.max(16, (count / max) * 100)}%` }}
              />
            </div>
            <span className={cn("text-xs", today ? "font-semibold text-indigo-700" : "text-slate-400")}>
              {format(day, "EEE")}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
